/**
 * TeleTime POS - Entry Point
 * Application bootstrap with providers and error handling
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { del } from 'idb-keyval';

// Every till that ran an earlier build still holds the manager PIN hash table and the
// offline approval queue in IndexedDB. Neither is used any more (2026-09-22), and waiting
// for a full device logout to clear them would leave the hashes sitting on the register.
// Purge on every boot; harmless once they are gone.
del('manager-pin-cache').catch(() => {});
del('offline-approval-queue').catch(() => {});

// Providers
import { AuthProvider } from './context/AuthContext';
import { RegisterProvider } from './context/RegisterContext';
// CartContext removed — cart state now managed by Zustand (store/unifiedStore.js)
import { ManagerApprovalProvider } from './components/Checkout/ManagerApprovalProvider';
import { BatchEmailProvider } from './contexts/BatchEmailContext';
import { CommissionProvider } from './context/CommissionContext';

// Components
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Error tracking
import errorTracker from './services/ErrorTracker';

// Service worker update plumbing (Piece A) + banner (Piece B)
import { getSwUpdater } from './swUpdater';
import { BuildStamp } from './components/BuildStamp';
import { UpdateBanner } from './components/UpdateBanner';

// Offline cache
import { prefillProductCache, prefillCustomerCache, refreshCache } from './services/offlineCacheService';
import { getSyncManager } from './store/offlineSync';
import { startEhfSync } from './store/ehfSync';

// Styles
import './index.css';

// ============================================================================
// ENVIRONMENT LOGGING
// ============================================================================

if (import.meta.env.DEV) {
  console.log('[TeleTime POS] Development mode');
}

// ============================================================================
// CLIENT ERROR TRACKING — install global listeners before React renders
// ============================================================================
errorTracker.install();

// Flush any queued errors when the page is about to unload
window.addEventListener('beforeunload', () => errorTracker.flush());

// ============================================================================
// SERVICE WORKER REGISTRATION (for future PWA support)
// ============================================================================

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    // Piece A: the updater owns the update lifecycle — a single guarded
    // reload on controllerchange, plus the waiting-worker API (applyUpdate /
    // onUpdateWaiting) that the update banner (B) and safe-point auto-apply
    // (C) consume. Activation is page-controlled: the SW skipWaiting()s only
    // when messaged.
    const updater = getSwUpdater();
    navigator.serviceWorker.register('/sw.js')
      .then(registration => {
        updater.setRegistration(registration);
        // Piece B: notice deploys without a navigation — poll every
        // UPDATE_POLL_INTERVAL_MS plus on focus/online. Idempotent.
        updater.startPolling();
        updater.onUpdateWaiting(() => {
          console.log('[TeleTime POS] New version installed and waiting — banner shown; apply via tap or getSwUpdater().applyUpdate()');
        });
      })
      .catch(error => {
        console.error('[TeleTime POS] SW registration failed:', error);
      });
  });
}

// ============================================================================
// RENDER APPLICATION
// ============================================================================

/**
 * Application wrapper with all providers
 */
function AppWithProviders() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RegisterProvider>
            <ManagerApprovalProvider>
              <BatchEmailProvider>
                <CommissionProvider>
                  <App />
                  {/* Always-visible build id + update banner — outside App's
                      conditional returns so they show on init/lock/main
                      screens alike */}
                  <BuildStamp />
                  <UpdateBanner />
                </CommissionProvider>
              </BatchEmailProvider>
            </ManagerApprovalProvider>
          </RegisterProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

// Mount the application
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    '[TeleTime POS] Root element not found. Make sure there is a <div id="root"></div> in your index.html'
  );
}


const root = ReactDOM.createRoot(rootElement);
root.render(<AppWithProviders />);


// ============================================================================
// EHF — every cart line's fee comes from the server (see store/ehfSync.js)
// ============================================================================

try {
  startEhfSync();
} catch (err) {
  console.warn('[TeleTime POS] EHF sync failed to start:', err);
}

// ============================================================================
// OFFLINE CACHE PREFILL — populate Dexie with products/customers on boot
// ============================================================================

try {
  if (navigator.onLine) {
    // Delay prefill to avoid competing with initial page loads
    setTimeout(() => {
      prefillProductCache().catch(() => {});
      prefillCustomerCache().catch(() => {});
    }, 5000);
  }

  // Refresh cache when connection is restored
  const syncManager = getSyncManager();
  syncManager.addListener((event) => {
    if (event === 'online') {
      refreshCache();
    }
  });
} catch (err) {
  console.warn('[TeleTime POS] Offline cache init failed (non-blocking):', err);
}

// ============================================================================
// HOT MODULE REPLACEMENT (Development)
// ============================================================================

if (import.meta.hot) {
  import.meta.hot.accept();
}
